<script lang="ts">
	import type { MaterialProperty } from './types/materialProperty'
	import { DefaultMaterialProperty } from './types/materialProperty'
	import { modals } from 'svelte-modals'
	import MaterialPropertyModal from './MaterialPropertyModal.svelte'
	import { mmpvState } from './mmpv.state.svelte'
	import { t } from '$i18n/i18n'
	import Modal from '#lib/organisms/Modal.svelte'

	interface Props {
		property: MaterialProperty
		idx: number // idx is important for the color scheme
		isEditable?: boolean
		onUpdateMaterialProperty?: (propertyId: string) => void
		onRemoveProperty?: (property: MaterialProperty) => void
	}

	let {
		property, // we only use this to show values, so no binding
		idx,
		isEditable = true,
		onUpdateMaterialProperty,
		onRemoveProperty
	}: Props = $props()

	function handleCardKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			editProperty()
		}
	}

	function handleDelete() {
		modals.open(Modal, {
			title: $t('UI.mmpv.deletePropertyFromMaterial.modal.title'),
			message: $t('UI.mmpv.deletePropertyFromMaterial.modal.text', {
				property: property?.displayName
			}),
			onDeny: () => {
				onRemoveProperty?.(property)
			},
			denyText: $t('UI.mmpv.deletePropertyFromMaterial.modal.btn')
		})
	}

	function handleRemoveKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			event.stopPropagation()
			handleDelete()
		}
	}

	function editProperty() {
		modals.open(MaterialPropertyModal, {
			materialProperty: {
				...DefaultMaterialProperty(),
				...mmpvState.getMaterialProperty(property.id)
			},
			isNew: false,
			parent: null,
			materialId: mmpvState.selectedMaterial.id,
			updateMaterial: (propertyId: string) => {
				onUpdateMaterialProperty?.(propertyId)
			}
		})
	}
</script>

{#if isEditable}
	<div
		class="material-property-card color-{idx % 3}"
		role="button"
		tabindex="0"
		title={property.name}
		onclick={editProperty}
		onkeydown={handleCardKeydown}
	>
		<span title={property.name}>{property.name}</span>
		<div
			class="remove-icon"
			role="button"
			tabindex="0"
			aria-label={$t('UI.delete')}
			onclick={(event) => {
				event.stopPropagation()
				handleDelete()
			}}
			onkeydown={handleRemoveKeydown}
		></div>
	</div>
{/if}
{#if !isEditable}
	<div class="material-property-card disabled" title={property.name}>
		<span>{property.name}</span>
	</div>
{/if}

<style>
	@import '$styles/global.scss';

	.material-property-card {
		min-height: 66px;
		width: 100%;
		height: 100%;
		border-radius: 4px;
		padding: 0.75rem 2rem 0.75rem 0.75rem;
		position: relative;
		display: flex; /* adjusts that the font is directly on top and left and does not use too much space for two rows */
		& > span {
			font-family: Roboto, sans-serif;
			line-height: 14px;
			font-weight: 400;
			font-size: 0.75rem;
			color: white;

			text-align: start;
			display: -webkit-box;
			-webkit-line-clamp: 3;
			-webkit-box-orient: vertical;
			overflow: hidden;
		}

		&:hover {
			cursor: pointer;
		}
	}

	.remove-icon {
		width: 20px;
		height: 20px;
		position: absolute;
		top: 0.5rem;
		right: 0.5rem;
		background: url('@/icons/icon_cancel_20_white.svg') no-repeat;
		background-size: contain;
	}

	.color-0 {
		background-color: #6ac3df;
	}

	.color-1 {
		background-color: #56a2ba;
	}

	.color-2 {
		background-color: #427e92;
	}

	.disabled {
		background-color: var(--color-gray-400);
	}
</style>
